import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Search, Dumbbell, Filter } from "lucide-react";
import { exerciseApi } from "@/lib/api";
import type { ExerciseLibraryItem } from "@/types";

const bodyParts = [
  "all",
  "back",
  "cardio",
  "chest",
  "lower arms",
  "lower legs",
  "neck",
  "shoulders",
  "upper arms",
  "upper legs",
  "waist",
];

function ExerciseCard({ exercise }: { exercise: ExerciseLibraryItem }) {
  return (
    <Link to={`/exercises/${exercise.id}`}>
      <Card className="overflow-hidden group hover:border-amber-500/50 transition-colors">
        <div className="aspect-square bg-card relative overflow-hidden">
          <img
            src={exercise.gifUrl}
            alt={exercise.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        </div>
        <div className="p-4">
          <div className="flex gap-2 mb-2">
            <Badge variant="secondary" className="text-xs">
              {exercise.bodyPart}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {exercise.target}
            </Badge>
          </div>
          <h3 className="font-semibold capitalize text-sm line-clamp-2">
            {exercise.name}
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            {exercise.equipment}
          </p>
        </div>
      </Card>
    </Link>
  );
}

export default function Exercises() {
  const [search, setSearch] = useState("");
  const [selectedBodyPart, setSelectedBodyPart] = useState("all");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: exercises, isLoading } = useQuery({
    queryKey: ["exercises", selectedBodyPart, debouncedSearch],
    queryFn: async () => {
      if (debouncedSearch) {
        return exerciseApi.searchExercises(debouncedSearch);
      }
      return exerciseApi.getExercises(selectedBodyPart);
    },
  });

  return (
    <div className="min-h-screen pt-24 pb-12 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            Exercise Library
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Browse 1000+ exercises with GIF demonstrations. Search by name, muscle group, or equipment.
          </p>
        </div>

        {/* Search */}
        <div className="max-w-xl mx-auto mb-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search exercises..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-12"
            />
          </div>
        </div>

        {/* Body Part Filter */}
        <div className="mb-8">
          <ScrollArea className="w-full whitespace-nowrap">
            <div className="flex gap-2 p-1">
              {bodyParts.map((part) => (
                <Button
                  key={part}
                  variant={selectedBodyPart === part ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedBodyPart(part)}
                  className={
                    selectedBodyPart === part
                      ? "bg-amber-500 text-black hover:bg-lime-500"
                      : ""
                  }
                >
                  {part === "all" ? (
                    <>
                      <Filter className="w-4 h-4 mr-1" />
                      All
                    </>
                  ) : (
                    part
                  )}
                </Button>
              ))}
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </div>

        {/* Results Count */}
        {!isLoading && exercises && (
          <p className="text-sm text-muted-foreground mb-4">
            Showing {exercises.length} exercises
          </p>
        )}

        {/* Exercises Grid */}
        {isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {Array.from({ length: 10 }).map((_, i) => (
              <Card key={i} className="overflow-hidden">
                <Skeleton className="aspect-square" />
                <div className="p-4 space-y-2">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-full" />
                </div>
              </Card>
            ))}
          </div>
        ) : exercises?.length === 0 ? (
          <div className="text-center py-12">
            <Dumbbell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No exercises found</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {exercises?.map((exercise) => (
              <ExerciseCard key={exercise.id} exercise={exercise} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
